import React from 'react';
import {
  Paper,
  Box,
  Typography,
  MenuItem,
  Stack,
  SxProps,
  CircularProgress,
  Theme,
  ClickAwayListener,
  Popper,
  PopperProps
} from '@mui/material';
import { Button } from '../buttons/Button';
import { Check } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { SearchInput } from 'common/ui/SearchInput';
import { HighlightedText } from 'common/form/components/HighlightedText';
import { ClearLink } from 'common/form/components/ClearLink';

export type DropdownMenuOption = {
  label: string | React.ReactNode;
  subLabel?: string;
  value: string;
  startComponents?: React.ReactNode;
  extraData?: string[];
};

type DropdownListSearchOptions = {
  placeholder?: string;
  searchQuery?: string;
  onChange: (value: string) => void;
  // Minimum characters before searching; below this a hint is shown instead of
  // results. `0`/omitted disables the gate.
  minSearchLength?: number;
};

type DropdownListStickyButton = {
  text: string;
  startIcon?: React.ReactNode;
  onClick: () => void;
};

type DropdownListConfirmActions = {
  applyText?: string;
  cancelText?: string;
  onApply: () => void;
  onCancel: () => void;
  // Empties the pending selection; nothing is filtered until applied.
  onClear?: () => void;
};

export type DropdownListProps = {
  popperProps: PopperProps;
  options: DropdownMenuOption[];
  searchOptions?: DropdownListSearchOptions;
  sxProps?: SxProps;
  selectedValues?: string[];
  stickyButton?: DropdownListStickyButton;
  isLoading?: boolean;
  // Cap for the list's height. Defaults to a compact 300px; pass a taller value
  // (e.g. a `calc(100vh - …)`) to let the list fill the viewport. The list
  // scrolls internally beyond this.
  maxHeight?: number | string;
  // Shown when there is nothing left to pick and no search to blame for it.
  emptyMessage?: string;
  headerContent?: React.ReactNode;
  showCheckboxes?: boolean;
  showSelectedCount?: boolean;
  // Long labels wrap at 30ch rather than widen the menu.
  wrapLabels?: boolean;
  confirmActions?: DropdownListConfirmActions;
  onSelect: (value: string) => void;
  onClickAway: (event: MouseEvent | TouchEvent) => void;
};

const paperSx: SxProps<Theme> = {
  mt: 1,
  mb: 2,
  maxHeight: 300,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'
};

const selectedCountSx: SxProps<Theme> = {
  px: 1.2,
  pb: 1
};

const optionSx = (
  hasSubLabel: boolean,
  wrapLabels: boolean
): SxProps<Theme> => ({
  py: '10px',
  display: 'flex',
  alignItems: hasSubLabel ? 'flex-start' : 'center',
  gap: '8px',
  ...(wrapLabels && { whiteSpace: 'normal' })
});

const wrappedLabelSx: SxProps<Theme> = {
  maxWidth: '30ch',
  minWidth: 0,
  overflowWrap: 'anywhere'
};

const confirmActionsSx: SxProps<Theme> = {
  p: 1.5,
  flexShrink: 0,
  borderTop: '1px solid',
  borderColor: 'divider'
};

export const DropdownList: React.FC<DropdownListProps> = ({
  popperProps,
  options,
  searchOptions,
  sxProps,
  selectedValues = [],
  stickyButton,
  isLoading = false,
  maxHeight,
  emptyMessage,
  headerContent,
  showCheckboxes = false,
  showSelectedCount = false,
  wrapLabels = false,
  confirmActions,
  onSelect,
  onClickAway
}) => {
  const searchQuery = searchOptions?.searchQuery ?? '';
  const minSearchLength = searchOptions?.minSearchLength ?? 0;
  // A non-empty query below the minimum shows a hint instead of results.
  const isBelowMinLength =
    searchQuery.length > 0 && searchQuery.length < minSearchLength;
  const showNoResults =
    searchOptions &&
    options.length === 0 &&
    searchQuery &&
    !isLoading &&
    !isBelowMinLength;
  const showEmptyMessage =
    emptyMessage && options.length === 0 && !searchQuery && !isLoading;

  return (
    <Popper {...popperProps}>
      <ClickAwayListener onClickAway={onClickAway}>
        <Box sx={{ maxHeight: maxHeight ?? '400px' }}>
          <Paper
            elevation={0}
            sx={{ ...paperSx, ...(maxHeight ? { maxHeight } : {}), ...sxProps }}
          >
            {headerContent && (
              <Box
                sx={{ p: 1, display: 'flex', justifyContent: 'center' }}
                // Must not reach the option list beneath.
                onClick={(event) => event.stopPropagation()}
              >
                {headerContent}
              </Box>
            )}
            {searchOptions !== undefined && (
              <Box sx={{ p: 1 }}>
                <SearchInput
                  placeholder={searchOptions.placeholder || 'Search...'}
                  defaultValue={searchQuery}
                  onChange={searchOptions.onChange}
                />
              </Box>
            )}
            {showSelectedCount && (
              <Stack
                direction="row"
                justifyContent="center"
                gap={1}
                sx={selectedCountSx}
              >
                <Typography
                  color="text.secondary"
                  data-testid="dropdown-selected-count"
                >
                  {selectedValues.length} selected
                </Typography>
                {confirmActions?.onClear && selectedValues.length > 0 && (
                  <ClearLink
                    onClearClicked={confirmActions.onClear}
                    clearLabel="selection"
                  />
                )}
              </Stack>
            )}
            <Box sx={{ overflow: 'auto' }}>
              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isLoading && isBelowMinLength && (
                <Typography sx={{ color: 'text.secondary', px: 1.2, py: 2 }}>
                  Type at least {minSearchLength} characters to search
                </Typography>
              )}
              {!isLoading && showNoResults && (
                <Typography sx={{ color: 'error.main', px: 1.2, py: 2 }}>
                  No options found with '{searchQuery}'
                </Typography>
              )}
              {!isLoading && showEmptyMessage && (
                <Typography sx={{ color: 'customGrey.dark', px: 1.2, py: 2 }}>
                  {emptyMessage}
                </Typography>
              )}
              {!isLoading &&
                !showNoResults &&
                !isBelowMinLength &&
                options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <MenuItem
                      key={option.value}
                      sx={optionSx(!!option.subLabel, wrapLabels)}
                      onClick={() => onSelect(option.value)}
                      selected={isSelected}
                      data-testid={`dropdown-menu-option-${option.value}`}
                    >
                      {showCheckboxes && (
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          disableRipple
                          tabIndex={-1}
                          sx={{ p: 0 }}
                        />
                      )}
                      {!showCheckboxes && isSelected && (
                        <Check color="primary" />
                      )}
                      {option.startComponents && (
                        <Stack direction="row" gap={1}>
                          {option.startComponents}
                        </Stack>
                      )}
                      <Box sx={wrapLabels ? wrappedLabelSx : undefined}>
                        {searchOptions && (
                          <HighlightedText
                            text={option.label as string}
                            highlight={searchQuery}
                            extraData={option.extraData}
                          />
                        )}
                        {!searchOptions && option.label}
                        {option.subLabel && (
                          <Typography
                            variant="body2"
                            color="customGrey.dark"
                            fontWeight={500}
                          >
                            {option.subLabel}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
            </Box>
            {confirmActions && (
              <Stack direction="row" gap={1.5} sx={confirmActionsSx}>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={confirmActions.onApply}
                  data-testid="dropdown-apply"
                >
                  {confirmActions.applyText ?? 'Apply'}
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={confirmActions.onCancel}
                  data-testid="dropdown-cancel"
                >
                  {confirmActions.cancelText ?? 'Cancel'}
                </Button>
              </Stack>
            )}
            {stickyButton && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="tertiary"
                  fullWidth
                  startIcon={stickyButton.startIcon}
                  onClick={stickyButton.onClick}
                >
                  {stickyButton.text}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
